import React from 'react';
import { Heading4, Heading5 } from '~/components/atom/Typography';
import SelectBlock from '~/components/molecule/SelectBlock';
import SectionContainer from '../SectionContainer';
import { TEXT } from '../../constants';
import type { GetLfaConversionDataQuery } from '~/graphql/types';
import type {
  UpdateSettingsAction,
  UpdateSettingsState,
} from '../../reducers/settingsReducer';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Divider from '~/components/atom/Divider';
import GridContainer from '~/components/atom/GridContainer';
import LocationItem from './components/LocationItem';
import type { TouchedFields } from '../../hooks/useSettingsValidation';

const text = {
  selectAvailableLocationOptions:
    'Selecteer beschikbare locatieopties voor deze dienst',
};

export type Props = {
  dataTestId?: string;
  className?: string;
  allServices: GetLfaConversionDataQuery['getLfaConversionData']['allServices'];
  updateSettings: UpdateSettingsState;
  dispatch: React.Dispatch<UpdateSettingsAction>;
  setFieldTouched: (field: keyof TouchedFields) => void;
};

const SelectServicesSection: React.FCC<Props> = ({
  dataTestId,
  className,
  allServices,
  updateSettings,
  dispatch,
  setFieldTouched,
}) => (
  <SectionContainer dataTestId={dataTestId} className={className}>
    <Heading4 size="m">{TEXT.servicesHeader} </Heading4>
    <GridContainer width="100%" gap="m" columns="repeat(2, 1fr)">
      {allServices.map(option => {
        const isSelected = updateSettings?.services.some(
          service => service.id === option.id,
        );

        return (
          <SelectBlock
            type="checkbox"
            key={option.id}
            checkboxTitle={option.name}
            description={option.description}
            checked={isSelected}
            margin={[null]}
            onClick={() => {
              setFieldTouched('locations');
              dispatch({
                type: 'TOGGLE_SERVICE',
                payload: { service: option },
              });
            }}
          >
            {isSelected && (
              <JustificationContainer
                width="100%"
                padding={['s', 'xl', null, 'xl']}
                direction="column"
                gap="xs"
                onClick={e => {
                  e.stopPropagation();
                }}
                style={{ cursor: 'initial' }}
              >
                <Divider margin={[null]} />
                <Heading5 margin={[null]} fontWeight="regular" required>
                  {text.selectAvailableLocationOptions}
                </Heading5>
                <JustificationContainer
                  direction="column"
                  gap="xs"
                  width="100%"
                >
                  {option.locations.map((location, index) => {
                    const currentLocations =
                      updateSettings.services.find(
                        service => service.id === option.id,
                      )?.locations || [];

                    const isLocationSelected = currentLocations.some(
                      l => l.type === location.type && l.enabled,
                    );

                    const currentLocation = currentLocations.find(
                      l => l.type === location.type,
                    );

                    return (
                      <LocationItem
                        setFieldTouched={setFieldTouched}
                        key={`${option.id}-${location.type}-${index}`}
                        option={option}
                        location={location}
                        isLocationSelected={isLocationSelected}
                        currentLocation={currentLocation}
                        dispatch={dispatch}
                        isLast={index === option.locations.length - 1}
                      />
                    );
                  })}
                </JustificationContainer>
              </JustificationContainer>
            )}
          </SelectBlock>
        );
      })}
    </GridContainer>
  </SectionContainer>
);

export default SelectServicesSection;
